import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import DialogComponent from "../../../../../../common/components/functional/dialogs/DialogComponent";
import isEmpty from "../../../../../../common/helpers/isEmpty";
import { ModalState } from "../../../../types/types";
import {
  SHOW_ONLY_FOR_HOURLY_BILLING_LABEL_FOR_CATEGORIES,
  PRICING_CATEGORY_KEYS,
  PricingCategory,
  PricingCategorySelection,
} from "../../../constants";
import LabelInputSelectRow from "../../../form/LabelInputSelectRow";
import { StyledFormSection } from "../../../styles";
import PricingsByExpenseTypeForm from "./PricingsByExpenseTypeForm";

type InputChangeHandlerProps = {
  fieldName: string;
  inputPath: string;
  newInput: string;
};

type SelectChangeHandlerProps = {
  fieldName: string;
  inputPath: string;
  selectPath: string;
  newSelection: string;
};

interface Props {
  tesId: string | null;
  disabled?: boolean;
  canEdit: boolean;
}

const START_PATH = "categories";
const DRAG_HANDLE_SELECTOR = "drag-handle-work-and-absences";

const WorkAndAbsencesSection = ({ tesId, disabled, canEdit }: Props) => {
  const { t } = useTranslation("customers");
  const { getValues, setValue, watch, setError, clearErrors } =
    useFormContext();

  const [modalState, setModalState] = useState<ModalState>({
    show: false,
    id: "",
  });

  const onOpenModal = (id?: string) => setModalState({ show: true, id });
  const onCloseModal = () =>
    setModalState((prevState) => ({ ...prevState, show: false }));
  const onModalExited = () => {
    setModalState({ show: false, id: "" });
  };

  const getCategoryObjectPath = (categoryKey: PricingCategory) => {
    return `${START_PATH}.${categoryKey}`;
  };

  const getPricingBySalaryTypeModalDefaultProps = (
    categoryKey: PricingCategory,
  ) => {
    const hideFixedPriceAndOverride =
      categoryKey === PricingCategory.HolidaySalary;
    const showLabel =
      SHOW_ONLY_FOR_HOURLY_BILLING_LABEL_FOR_CATEGORIES.includes(categoryKey);
    const categoryObjectPath = getCategoryObjectPath(categoryKey);
    return {
      tesId: tesId!, // cannot be null here
      mainFormPropertyKey: categoryObjectPath,
      expenseTypeCategoryFilter: categoryKey,
      modalTitle: t("contract.pricingBySalaryType"),
      rowLabel: t("contract.salaryType"),
      inputLabel: t("contract.searchSalaryTypes"),
      addButtonLabel: t("contract.pricingBySalaryType"),
      confirmDialogTitle: t("contract.pricingBySalaryTypeDeleteConfirmTitle"),
      confirmDialogContentText: t(
        "contract.pricingBySalaryTypeDeleteConfirmText",
      ),
      onClose: onCloseModal,
      disabled,
      canEdit,
      dragHandleSelector: DRAG_HANDLE_SELECTOR,
      hideFixedPriceOption: hideFixedPriceAndOverride,
      hideOverrideOption: hideFixedPriceAndOverride,
      showOnlyForHourlyBillingLabel: showLabel,
    };
  };

  const getModalContent = () => {
    switch (modalState.id) {
      case PricingCategory.BaseHours:
        return (
          <PricingsByExpenseTypeForm
            {...getPricingBySalaryTypeModalDefaultProps(
              PricingCategory.BaseHours,
            )}
          />
        );
      case PricingCategory.Supplements:
        return (
          <PricingsByExpenseTypeForm
            {...getPricingBySalaryTypeModalDefaultProps(
              PricingCategory.Supplements,
            )}
          />
        );
      case PricingCategory.TaxFreeCompensations:
        return (
          <PricingsByExpenseTypeForm
            {...getPricingBySalaryTypeModalDefaultProps(
              PricingCategory.TaxFreeCompensations,
            )}
            modalTitle={t("contract.pricingByExpenseType")}
            rowLabel={t("contract.expenseType")}
            inputLabel={t("contract.searchExpenseTypes")}
            addButtonLabel={t("contract.pricingByExpenseType")}
            confirmDialogTitle={t(
              "contract.pricingByExpenseTypeDeleteConfirmTitle",
            )}
            confirmDialogContentText={t(
              "contract.pricingByExpenseTypeDeleteConfirmText",
            )}
          />
        );
      case PricingCategory.SickLeave:
        return (
          <PricingsByExpenseTypeForm
            {...getPricingBySalaryTypeModalDefaultProps(
              PricingCategory.SickLeave,
            )}
          />
        );
      // NOTE: "Sick leave billing rules" not in Phase 1.
      // case PricingModalType.SickLeaveBillingRules:
      //   return <SickLeaveBillingRulesForm onClose={onCloseModal} />;
      case PricingCategory.MidweekHolidays:
        return (
          <PricingsByExpenseTypeForm
            {...getPricingBySalaryTypeModalDefaultProps(
              PricingCategory.MidweekHolidays,
            )}
          />
        );
      case PricingCategory.ShortTimeWorkingScheme:
        return (
          <PricingsByExpenseTypeForm
            {...getPricingBySalaryTypeModalDefaultProps(
              PricingCategory.ShortTimeWorkingScheme,
            )}
          />
        );
      case PricingCategory.HolidaySalary:
        return (
          <PricingsByExpenseTypeForm
            {...getPricingBySalaryTypeModalDefaultProps(
              PricingCategory.HolidaySalary,
            )}
          />
        );
      default:
        return <></>;
    }
  };

  const handleInputChange = ({
    fieldName,
    inputPath,
    newInput,
  }: InputChangeHandlerProps) => {
    !newInput ? setError(fieldName, {}) : clearErrors(fieldName);
    setValue(inputPath, newInput, { shouldDirty: true });
  };

  const handleSelect = ({
    fieldName,
    inputPath,
    selectPath,
    newSelection,
  }: SelectChangeHandlerProps) => {
    newSelection === PricingCategorySelection.NotBilled
      ? clearErrors(fieldName)
      : setError(fieldName, {});
    setValue(selectPath, newSelection, { shouldDirty: true });
    // Reset input value when selection is changed
    setValue(inputPath, "", { shouldDirty: true });
  };

  const getDefaultRowProps = (categoryKey: PricingCategory) => {
    const categoryObjectPath = getCategoryObjectPath(categoryKey);
    const multiplierPath = `${categoryObjectPath}.multiplier`;
    const typePath = `${multiplierPath}.type`;
    const valuePath = `${multiplierPath}.value`;
    const expensesPath = `${categoryObjectPath}.expenses`;
    return {
      inputValue: watch(valuePath),
      inputRequired: true,
      inputWidth: 1.2,
      inputUnitWidth: 0.2,
      onInputChange: (newInput: string) =>
        handleInputChange({
          fieldName: categoryKey,
          inputPath: valuePath,
          newInput,
        }),
      defaultSelection: getValues(typePath),
      onSelectionChange: (newSelection: string) =>
        handleSelect({
          fieldName: categoryKey,
          inputPath: valuePath,
          selectPath: typePath,
          newSelection,
        }),
      showButton:
        watch(typePath) !== PricingCategorySelection.NotBilled && !!tesId,
      showViewButton: watch(expensesPath),
      editButtonText: t("contract.pricingBySalaryType"),
      viewButtonText: t("contract.showPricingBySalaryTypeInformation"),
      onButtonClick: () => onOpenModal(categoryKey),
      disabled: disabled,
    };
  };

  // Set errors for categories which don't have valid values
  useEffect(() => {
    PRICING_CATEGORY_KEYS.forEach((categoryKey) => {
      const categoryObject = getValues(getCategoryObjectPath(categoryKey));
      const categoryObjectMultiplier = categoryObject?.multiplier;
      if (
        categoryObjectMultiplier?.type ===
          PricingCategorySelection.Multiplier &&
        isEmpty(categoryObjectMultiplier?.value)
      ) {
        setError(categoryKey, {});
      }
    });
    // Call useEffect only once onmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledFormSection title={t("contract.workAndAbsences")}>
      <LabelInputSelectRow
        label={t("settings:BaseHours")}
        selectOptions={[PricingCategorySelection.Multiplier]}
        labelTooltip={t("contract.baseHoursTooltip")}
        {...getDefaultRowProps(PricingCategory.BaseHours)}
      />
      <LabelInputSelectRow
        label={t("settings:Supplements")}
        selectOptions={[PricingCategorySelection.Multiplier]}
        {...getDefaultRowProps(PricingCategory.Supplements)}
      />
      <LabelInputSelectRow
        label={t("settings:TaxFreeCompensations")}
        {...getDefaultRowProps(PricingCategory.TaxFreeCompensations)}
        editButtonText={t("contract.pricingByExpenseType")}
        viewButtonText={t("contract.showPricingByExpenseTypeInformation")}
      />
      <LabelInputSelectRow
        label={t("customers:contract.SickLeaves")}
        {...getDefaultRowProps(PricingCategory.SickLeave)}
        // NOTE: "Sick leave billing rules" not in Phase 1.
        // NOTE2: After the change that every row (category) has own button, we need to
        // make changes here to make "Sick leave billing rules" possible.
        // showButton
        // showViewButton={!!watch("sickLeaveBillingRules")} // TODO rule for showing correct mode
        // TODO need to show view button here, but fields should not be disabled?
        // -> If this is the case, need to modify <LabelInputSelectRow /> component
        // editButtonText={t("contract.sickLeaveBillingRules")}
        // viewButtonText={t("contract.showSickLeaveBillingRulesInformation")}
        // onButtonClick={() =>
        //   onOpenModal(PricingModalType.SickLeaveBillingRules)
        // }
      />
      <LabelInputSelectRow
        label={t("settings:MidweekHolidays")}
        {...getDefaultRowProps(PricingCategory.MidweekHolidays)}
      />
      <LabelInputSelectRow
        label={t("settings:ShortTimeWorkingScheme")}
        {...getDefaultRowProps(PricingCategory.ShortTimeWorkingScheme)}
      />
      <LabelInputSelectRow
        label={t("customers:contract.HolidaySalary")}
        {...getDefaultRowProps(PricingCategory.HolidaySalary)}
      />
      <LabelInputSelectRow
        label={t("settings:HolidayBonus")}
        {...getDefaultRowProps(PricingCategory.HolidayBonus)}
        showButton={false}
      />
      <DialogComponent
        open={modalState.show}
        onClose={onCloseModal}
        sx={{ display: "flex", maxHeight: "80%" }}
        dragHandleSelector={DRAG_HANDLE_SELECTOR}
        TransitionProps={{ onExited: onModalExited }}
      >
        {getModalContent()}
      </DialogComponent>
    </StyledFormSection>
  );
};

export default WorkAndAbsencesSection;
